import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, Card, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import carteraService, { CarteraPagedResult } from '../../services/carteraService';

const Carteras: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [carterasData, setCarterasData] = useState<CarteraPagedResult>({
    items: [], totalCount: 0, pageNumber: 1, pageSize: 10, totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);

  const fetchCarteras = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      setLoading(true);
      const rawSearch = (search ?? '').trim();
      const compact = rawSearch.replace(/\s+/g, '');
      const isNumericSearch = compact !== '' && /^\d+$/.test(compact);
      
      if (isNumericSearch) {
        try {
          const nro = Number(compact);
          const cartera = await carteraService.getCartera(nro);
          const adjustedData = {
            items: cartera ? [cartera] : [],
            totalCount: cartera ? 1 : 0,
            pageNumber: 1,
            pageSize,
            totalPages: 1
          };
          setCarterasData(adjustedData);
          return;
        } catch (error: any) {
          if (error?.response?.status === 404) {
            setCarterasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
            return;
          }
          console.error('Error al buscar por nroBanco:', error);
          setCarterasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
          return;
        }
      }
      
      const response = await carteraService.getCarteras({
        pageNumber: pageNumber,
        pageSize,
        searchTerm: rawSearch || undefined
      });
      setCarterasData(response);
    } catch (error: any) {
      console.error('Error al cargar carteras:', error);
      enqueueSnackbar('Error al cargar carteras', { variant: 'error' });
      setCarterasData({ items: [], totalCount: 0, pageNumber: 1, pageSize, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCarteras(page + 1, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = async (nroBanco: number) => {
    if (window.confirm('¿Está seguro de eliminar esta cartera?')) {
      try {
        await carteraService.deleteCartera(nroBanco);
        enqueueSnackbar('Cartera eliminada exitosamente', { variant: 'success' });
        fetchCarteras(page + 1, rowsPerPage, searchTerm);
      } catch (error: any) {
        console.error('Error al eliminar cartera:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar cartera', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Carteras
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/tesoreria/carteras/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              Nueva Cartera
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o número de cartera..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {carterasData.items.map((cartera) => (
                <Card 
                  key={cartera.nroBanco}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {cartera.nombreBanco?.trim() || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Nro: {cartera.nroBanco}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Editar cartera">
                          <IconButton
                            onClick={() => navigate(`/tesoreria/carteras/${cartera.nroBanco}`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#1976d2' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar cartera">
                          <IconButton
                            onClick={() => handleDelete(cartera.nroBanco)}
                            size="small"
                            sx={{
                              bgcolor: '#f44336',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#d32f2f' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <TablePagination
                component="div"
                count={carterasData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Nro. Cartera</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carterasData.items.map((cartera) => (
                    <TableRow 
                      key={cartera.nroBanco}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{cartera.nroBanco}</TableCell>
                      <TableCell>{cartera.nombreBanco?.trim() || '-'}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar cartera">
                            <IconButton
                              onClick={() => navigate(`/tesoreria/carteras/${cartera.nroBanco}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar cartera">
                            <IconButton
                              onClick={() => handleDelete(cartera.nroBanco)}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#d32f2f',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={carterasData.totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Carteras;

